import type { TextInputProps } from 'react-native';

import type { SurveyElement } from '../../model/surveySchema';

/**
 * The subset of `TextInput` props that decide which on-screen keyboard a
 * respondent gets, and how the platform treats what they type into it.
 *
 * `inputMode` and `enterKeyHint` are the cross-platform spellings. React Native
 * maps them onto `keyboardType` / `returnKeyType` internally, and
 * react-native-web passes them straight through to the DOM attributes of the
 * same name, so one value covers native and mobile web. The older prop names
 * still work on both, but react-native-web logs a deprecation warning for
 * `returnKeyType`, so prefer these.
 */
type SurveyKeyboardProps = Pick<
  TextInputProps,
  'inputMode' | 'enterKeyHint' | 'autoCapitalize' | 'autoCorrect' | 'autoComplete' | 'spellCheck'
>;

/**
 * A single line of prose: the phone's own defaults are right. Sentence casing
 * and autocorrect help, and Return dismisses the keyboard because `TextInput`
 * blurs a single-line field on submit.
 */
const PROSE_KEYBOARD: SurveyKeyboardProps = {
  inputMode: 'text',
  enterKeyHint: 'done',
  autoCapitalize: 'sentences',
  autoCorrect: true,
};

/**
 * Machine-shaped input — an address, a URL. Autocapitalizing or autocorrecting
 * these corrupts them, and the corruption is easy to miss because iOS applies
 * it as the respondent leaves the field.
 */
const VERBATIM_KEYBOARD: SurveyKeyboardProps = {
  enterKeyHint: 'done',
  autoCapitalize: 'none',
  autoCorrect: false,
  spellCheck: false,
};

/**
 * Keyboards for each `inputType` survey-core lets an author put on a `text`
 * question.
 *
 * Deliberately not exhaustive over survey-core's list: the date, time, month,
 * week, color, and range types describe native browser widgets that this client
 * does not render, so they fall through to the prose default and get an
 * ordinary keyboard rather than a keyboard that implies a picker exists.
 *
 * `number` maps to `decimal`, not `numeric`. A survey asks for percentages and
 * proportions as often as counts, and `numeric` gives Android an integers-only
 * pad that leaves a respondent who means 12.5 with no way to type it.
 */
const KEYBOARD_BY_INPUT_TYPE: Record<string, SurveyKeyboardProps> = {
  text: PROSE_KEYBOARD,
  number: { ...VERBATIM_KEYBOARD, inputMode: 'decimal' },
  tel: { ...VERBATIM_KEYBOARD, inputMode: 'tel', autoComplete: 'tel' },
  email: { ...VERBATIM_KEYBOARD, inputMode: 'email', autoComplete: 'email' },
  url: { ...VERBATIM_KEYBOARD, inputMode: 'url', autoComplete: 'url' },
  search: { ...PROSE_KEYBOARD, inputMode: 'search', enterKeyHint: 'search' },
};

/**
 * Chooses the keyboard for a single-line `text` question from the `inputType`
 * its author declared.
 *
 * survey-core treats `inputType` as presentation only — measured, not assumed:
 * `validate()` accepts the string "about 30" on a question typed `number`
 * without complaint. So this changes which keys a respondent is offered and
 * nothing about what the server will accept, and there is deliberately no
 * matching client-side format check. Adding one would reject answers the API
 * stores happily, which is the divergence this survey renderer keeps avoiding.
 *
 * An unrecognized or absent `inputType` yields the prose keyboard, so a survey
 * authored against a newer survey-core than this table knows about still gets a
 * usable field.
 */
export function getSurveyKeyboardProps(element: SurveyElement): SurveyKeyboardProps {
  if (!element.inputType) {
    return PROSE_KEYBOARD;
  }

  return KEYBOARD_BY_INPUT_TYPE[element.inputType] ?? PROSE_KEYBOARD;
}

/**
 * iOS keyboards drawn without a return key, so a respondent given one has no
 * key that dismisses it.
 *
 * `numeric` is deliberately absent: it maps to the numbers-and-punctuation
 * keyboard, which does have a return key. Only the pads lack one.
 */
const INPUT_MODES_WITHOUT_RETURN_KEY = ['decimal', 'tel'];

/**
 * Whether a field needs an explicit dismiss affordance because its keyboard
 * offers no way out on its own — a multiline answer, where Return inserts a
 * newline, or one of the numeric pads.
 */
export function needsKeyboardDismissAffordance(element: SurveyElement) {
  if (element.type === 'comment') {
    return true;
  }

  const { inputMode } = getSurveyKeyboardProps(element);

  return inputMode !== undefined && INPUT_MODES_WITHOUT_RETURN_KEY.includes(inputMode);
}

/**
 * The keyboard for a free-text answer that has no `inputType` to read: the
 * `comment` body of a long-form question, and the short text that accompanies a
 * selected Other option.
 *
 * A multiline field must not take `enterKeyHint`. Return inserts a newline
 * there, so labeling the key "done" would promise a dismissal that never
 * happens — pass `isMultiline` and the hint is dropped.
 */
export function getSurveyProseKeyboardProps(isMultiline = false): SurveyKeyboardProps {
  if (isMultiline) {
    const { enterKeyHint: _unusedOnMultiline, ...rest } = PROSE_KEYBOARD;

    return rest;
  }

  return PROSE_KEYBOARD;
}
